import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { trackPageView, trackSearchApps } from '/src/services/analytics/index.js';
import { Header } from '@/app/components/Header';
import { Footer } from '@/app/components/Footer';
import { AuthModal } from '@/app/components/AuthModal';
import { SearchForm } from '@/app/components/SearchForm';
import { SearchResults } from '@/app/components/SearchResults';

export function HomePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // App discovery state
  const [appSearchQuery, setAppSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  
  // Ref for search results section
  const resultsRef = useRef(null);

  // Track page view and check for pending app request after authentication
  useEffect(() => {
    // Track page view
    trackPageView('home');
    
    if (user) {
      const pendingRequest = sessionStorage.getItem('pendingAppRequest');
      if (pendingRequest) {
        try {
          const parsed = JSON.parse(pendingRequest);
          sessionStorage.removeItem('pendingAppRequest');
          
          // Navigate to submit page with the pending request data
          navigate('/submit', {
            state: {
              prefilledDescription: parsed.description,
              fromDiscovery: parsed.fromDiscovery
            }
          });
        } catch (error) {
          console.error('Error parsing pending app request:', error);
          sessionStorage.removeItem('pendingAppRequest');
        }
      }
    }
  }, [user, navigate]);

  const handleSignInNeeded = useCallback(() => {
    console.log('HomePage: User needs to sign in');
    setShowAuthModal(true);
  }, []);

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
    sessionStorage.removeItem('pendingAppRequest');
  };

  const handleAuthSuccess = () => {
    const pendingRequest = JSON.parse(sessionStorage.getItem('pendingAppRequest') || '{}');
    if (pendingRequest.fromDiscovery) {
      navigate('/submit', {
        state: {
          prefilledDescription: pendingRequest.description,
          fromDiscovery: true
        }
      });
      sessionStorage.removeItem('pendingAppRequest');
    }
  };

  // App discovery handlers
  const handleAppSearch = async (e) => {
    e.preventDefault();
    
    if (!appSearchQuery.trim()) {
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSearchResults(null);

    try {
      const response = await fetch('/api/search-apps', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appDescription: appSearchQuery.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      
      // Track the app search with proper analytics
      trackSearchApps(appSearchQuery.trim(), data);
      
      setSearchResults(data);
      
      // Auto-scroll to results after a brief delay to let them render
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 200);
      
    } catch (err) {
      console.error('Search error:', err);
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleRequestApp = () => {
    if (!user) {
      // Store the search query for after authentication
      sessionStorage.setItem('pendingAppRequest', JSON.stringify({
        description: appSearchQuery,
        fromDiscovery: true
      }));
      setShowAuthModal(true);
      return;
    }
    
    // Navigate to submit page with pre-filled description
    navigate('/submit', { 
      state: { 
        prefilledDescription: appSearchQuery,
        fromDiscovery: true 
      } 
    });
  };

  // Auto-resize textarea handler
  const handleTextareaChange = (e) => {
    setAppSearchQuery(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-t-transparent border-indigo-500 rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-20 h-20 border-4 border-t-transparent border-purple-400 rounded-full animate-spin opacity-60" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      <Header onSignInNeeded={handleSignInNeeded} />
      
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pt-12 pb-16 sm:pt-20 sm:pb-24 lg:pt-32 lg:pb-40">
            <div className="text-center max-w-6xl mx-auto">
              {/* Main Headline */}
              <div className="mb-10 sm:mb-12 lg:mb-16">
                <h1 className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-black tracking-tight mb-4 sm:mb-6 lg:mb-8 leading-tight px-4 sm:px-6 lg:px-0">
                  <span className="block bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent pb-2">
                    Find Any Solution You Need
                  </span>
                </h1>
                <p className="text-base sm:text-lg lg:text-2xl text-gray-600 font-light leading-relaxed max-w-4xl mx-auto px-4 sm:px-6 lg:px-0">
                  We'll search everywhere - web apps, mobile apps, desktop software, browser extensions, and more
                </p>
                <p className="text-sm sm:text-base text-gray-500 max-w-2xl mx-auto mt-4 px-4 sm:px-6 lg:px-0">
                  If we don't find it, you request it and if people like it, we'll get it built for you
                </p>
              </div>

              {/* Main Search Interface */}
              <SearchForm
                appSearchQuery={appSearchQuery}
                isSearching={isSearching}
                onSearchQueryChange={handleTextareaChange}
                onSearchSubmit={handleAppSearch}
              />
            </div>
          </div>
        </div>

        {/* Search Error */}
        {searchError && (
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-12">
            <div className="max-w-4xl mx-auto">
              <div className="bg-red-50/80 backdrop-blur-sm border-2 border-red-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.865-.833-2.635 0L4.168 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <p className="ml-3 text-red-800 font-medium">{searchError}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        <div ref={resultsRef}>
          <SearchResults
            searchResults={searchResults}
            searchQuery={appSearchQuery}
            onNewSearch={() => {
              setAppSearchQuery('');
              setSearchResults(null);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onRequestApp={handleRequestApp}
          />
        </div>

        {/* Navigation Links */}
        <div className="flex flex-col gap-3 mb-8">
          <Link
            to="/why-zaptopia"
            className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 p-4 transition-all duration-300 hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                  Why Zaptopia?
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Discover how we're revolutionizing app discovery
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <svg className="h-6 w-6 text-indigo-600 transform transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </Link>
          
          <Link
            to="/requests"
            className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 p-4 transition-all duration-300 hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                  View App Requests
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  See what others are looking for and vote on requests
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600 transform transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </Link>
        </div>
      </main>

      <Footer />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={handleCloseAuthModal} 
      />
    </div>
  );
}